import React from 'react';
import { CheckCircle2, Info, AlertTriangle, XCircle, X } from 'lucide-react';

type AlertType = 'success' | 'info' | 'warning' | 'error';

interface AlertProps {
    type?: AlertType;
    title?: string;
    message: string | React.ReactNode;
    onClose?: () => void;
    className?: string;
}

const typeStyles: Record<AlertType, { container: string; icon: React.ReactNode; title: string; message: string; close: string; border: string; }> = {
    success: {
        container: 'bg-emerald-50/80',
        icon: <CheckCircle2 className="w-5 h-5 text-emerald-600" />,
        title: 'text-emerald-900',
        message: 'text-emerald-800',
        close: 'text-emerald-700 hover:text-emerald-900',
        border: 'border-emerald-200',
    },
    info: {
        container: 'bg-sky-50/80',
        icon: <Info className="w-5 h-5 text-sky-600" />,
        title: 'text-sky-900',
        message: 'text-sky-800',
        close: 'text-sky-700 hover:text-sky-900',
        border: 'border-sky-200',
    },
    warning: {
        container: 'bg-amber-50/80',
        icon: <AlertTriangle className="w-5 h-5 text-amber-600" />,
        title: 'text-amber-900',
        message: 'text-amber-800',
        close: 'text-amber-700 hover:text-amber-900',
        border: 'border-amber-200',
    },
    error: {
        container: 'bg-rose-50/80',
        icon: <XCircle className="w-5 h-5 text-rose-600" />,
        title: 'text-rose-900',
        message: 'text-rose-800',
        close: 'text-rose-700 hover:text-rose-900',
        border: 'border-rose-200',
    },
};

const Alert: React.FC<AlertProps> = ({ type = 'info', title, message, onClose, className = '' }) => {
    const s = typeStyles[type];
    return (
        <div className={`w-full border ${s.border} ${s.container} rounded-xl p-4 shadow-sm ${className}`} role="alert">
            <div className="flex items-start gap-3">
                <div className="mt-0.5 shrink-0">
                    {s.icon}
                </div>
                <div className="flex-1">
                    {title && <div className={`font-semibold ${s.title}`}>{title}</div>}
                    <div className={`text-sm leading-relaxed ${s.message}`}>{message}</div>
                </div>
                {onClose && (
                    <button onClick={onClose} className={`${s.close} transition-colors`} aria-label="Fechar alerta">
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
};

export default Alert;


